import { isSpace, isWord, Match, Op } from "../expressions/index.ts";

/* eslint-disable no-case-declarations, @typescript-eslint/no-non-null-assertion */

export enum Token {
  Error = -2,
  End = -1,
  White = 0,
  Word = 1,
  Open = 2,
  Close = 3,
  Comma = 4,
  Or = 5,
  And = 6,
}

const ERROR_NONE = 0;
const ERROR_INVALID_CHAR = 1;
const ERROR_UNEXPECTED_CHAR = 2;
const ERROR_MISSING_QUOTE = 3;
const ERROR_UNEXPECTED_TOKEN = 4;
const ERROR_MISSING_LEFT_OPERAND = 5;
const ERROR_MISSING_RIGHT_OPERAND = 6;
const ERROR_UNMATCHED_OPEN = 7;
const ERROR_UNMATCHED_CLOSE = 8;
const ERROR_EMPTY_WORD = 9;
const ERROR_OVERLONG_WORD = 10;

export interface IParseOutput {
  begin(input: string): void;

  binaryExpr(op: Op, opToken: number, opLead: number, opStart: number, opEnd: number): void;

  matchExpr(word: string, matchLevel: number, lead: number, start: number, end: number): void;

  parenExpr(openLead: number, openStart: number, openEnd: number, lead: number, start: number, end: number): void;

  end(lead: number, start: number): void;
}

export class KeywordParser {
  // Expression to parse.
  private input: string;

  // Where to send parsed output.
  private output: IParseOutput;

  // The last value returned by readChar(), a full Unicode codepoint or a ERROR_*.
  private char = 0;

  // The position in input readChar() will read the next from.
  private charEnd = 0;

  // The last value returned by readToken(), either TOK_* or ERROR_*.
  private token = 0;

  // The position in input the current token started reading from.
  // Will be different to _start when there is leading whitespace.
  private lead = 0;

  // The position in input of the start of the current token.
  private start = 0;

  // The position in input of the end of the token (non-inclusive).
  private end = 0;

  private error = 0;

  // The value of the current token if it is TOK_WORD.
  // Does not include quotes or escaping.
  private word: null | string = null;

  // The OP_MATCH level of the current token if it is TOK_WORD, determined by quote type.
  private matchLevel = 0;

  get tokenString() {
    return this.input.substring(this.start, this.end);
  }

  constructor(input: string, output: IParseOutput) {
    this.input = input;
    this.output = output;
  }

  // == Parser ==
  public parseCore(): boolean {
    this.charEnd = 0;
    this.token = 0;
    this.error = 0;

    this.output.begin(this.input);
    // Prime lexer/parser with first char/token.
    this.nextChar();
    this.nextToken();
    if (this.parseExpr(Token.End)) {
      this.output.end(this.lead, this.start);
      return true;
    }
    return false;
  }

  public getErrorString() {
    switch (this.error) {
      case ERROR_NONE:
        return "";
      case ERROR_INVALID_CHAR:
        return "Invalid UTF-16 character";
      case ERROR_UNEXPECTED_CHAR:
        return "Character not allowed outside quotes";
      case ERROR_MISSING_QUOTE:
        return "Missing right quote";
      case ERROR_UNEXPECTED_TOKEN:
        return `Did not expect '${this.tokenString}'`;
      case ERROR_MISSING_LEFT_OPERAND:
        return "Missing left side of operator";
      case ERROR_MISSING_RIGHT_OPERAND:
        return "Missing right side of operator";
      case ERROR_UNMATCHED_OPEN:
        return "Unmatched open parenthesis";
      case ERROR_UNMATCHED_CLOSE:
        return "Unmatched close parenthesis";
      case ERROR_EMPTY_WORD:
        return "Empty phrase not allowed";
      case ERROR_OVERLONG_WORD:
        return "Phrase is too long";
      default:
        return `<unknown error code ${this.error}>`;
    }
  }

  // start: expr END
  // expr: words ((COMMA | OR | AND) words)*
  // words: WORD+ | OPEN expr CLOSE
  private parseExpr(endToken: number): boolean {
    if (!this.parseWords()) {
      return false;
    }

    while (endToken !== this.token) {
      switch (this.token) {
        default:
          this.error = ERROR_UNEXPECTED_TOKEN;
          return false;

        case Token.Error:
          return false;

        case Token.Close:
          this.error = ERROR_UNMATCHED_CLOSE;
          return false;

        case Token.Comma:
        case Token.Or:
        case Token.And:
          const opToken = this.token;
          const opLead = this.lead;
          const opStart = this.start;
          const opEnd = this.end;
          if (this.nextToken() < 0) {
            if (Token.End === (this.token as number)) {
              this.error = ERROR_MISSING_RIGHT_OPERAND;
            }
            return false;
          }
          if (!this.parseWords()) {
            return false;
          }
          this.output.binaryExpr(opToken === Token.And ? Op.And : Op.Or, opToken, opLead, opStart, opEnd);
          break;
      }
    }
    return true;
  }

  private parseWords(): boolean {
    switch (this.token) {
      default:
        this.error = ERROR_UNEXPECTED_TOKEN;
        return false;

      case Token.Error:
        return false;

      case Token.Word:
        this.output.matchExpr(this.word!, this.matchLevel, this.lead, this.start, this.end);
        while (Token.Word === this.nextToken()) {
          this.output.matchExpr(this.word!, this.matchLevel, this.lead, this.start, this.end);
          this.output.binaryExpr(Op.Or, Token.White, this.lead, this.lead, this.lead);
        }
        return true;

      case Token.Open:
        const openLead = this.lead;
        const openStart = this.start;
        const openEnd = this.end;
        // Eat OPEN
        this.nextToken();
        if (!this.parseExpr(Token.Close)) {
          if (Token.End === (this.token as number)) {
            this.error = ERROR_UNMATCHED_OPEN;
            // Restore error location.
            this.lead = openLead;
            this.start = openStart;
            this.end = openEnd;
          }
          return false;
        }
        this.output.parenExpr(openLead, openStart, openEnd, this.lead, this.start, this.end);
        // Eat CLOSE
        this.nextToken();
        return true;

      case Token.Close:
        this.error = ERROR_UNMATCHED_CLOSE;
        return false;

      case Token.Comma:
      case Token.Or:
      case Token.And:
        this.error = ERROR_MISSING_LEFT_OPERAND;
        return false;
    }
  }

  // ============ Lexer ============
  private nextChar(): number {
    this.end = this.charEnd;
    if (this.charEnd === this.input.length) {
      return (this.char = Token.End);
    }

    this.char = this.input.charCodeAt(this.charEnd++);

    // Support Surrogate pairs (String is UTF-16):
    if ((this.char & 0xd800) === 0xd800) {
      if (!(this.char & 400)) {
        --this.charEnd;
        this.error = ERROR_INVALID_CHAR;
        return (this.char = Token.Error);
      }
      if (this.charEnd !== this.input.length) {
        const low = this.input.charCodeAt(this.charEnd++);
        if ((low & 0xdc00) === 0xd800) {
          this.char = ((this.char & 0x3ff) << 10) | (low & 0x3ff);
          return this.char;
        }
      }
      --this.charEnd;
      this.error = ERROR_INVALID_CHAR;
      return (this.char = Token.Error);
    }
    return this.char;
  }

  private nextToken() {
    if (this.token < 0) {
      return this.token;
    }
    this.word = null;
    this.lead = this.start = this.end;
    if (this.char < 0) {
      return (this.token = this.char);
    }

    while (isSpace(this.char)) {
      if (this.nextChar() < 0) {
        this.start = this.end;
        return (this.token = this.char);
      }
    }

    this.start = this.end;

    switch (this.char) {
      case 0x28: // (
        this.nextChar();
        return (this.token = Token.Open);

      case 0x29: // )
        this.nextChar();
        return (this.token = Token.Close);

      case 0x2c: // ,
        this.nextChar();
        return (this.token = Token.Comma);

      case 0x22: // "
      case 0x2a: // *
        const quoteChar = this.char;

        this.word = "";
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const valueStart = this.charEnd;

          while (quoteChar !== this.nextChar()) {
            if (this.char < 0) {
              if (Token.End === (this.char as number)) {
                this.error = ERROR_MISSING_QUOTE;
              }
              return (this.token = Token.Error);
            }
          }

          const valueEnd = this.end;

          // Handle doubled for escaping, or eat closing quote.
          if (quoteChar !== this.nextChar()) {
            this.word += this.input.substring(valueStart, valueEnd);
            break;
          }
          this.word += this.input.substring(valueStart, this.end);
        }

        this.matchLevel = quoteChar === 0x22 ? Match.Full : Match.FullCase;

        if (0 === this.word.length) {
          this.error = ERROR_EMPTY_WORD;
          return (this.token = Token.Error);
        }

        if (this.word.length >= 128) {
          this.error = ERROR_OVERLONG_WORD;
          return (this.token = Token.Error);
        }

        return (this.token = Token.Word);

      default:
        if (!isWord(this.char)) {
          this.error = ERROR_UNEXPECTED_CHAR;
          return (this.token = Token.Error);
        }

        do {
          if (this.nextChar() < 0) {
            if (Token.End === this.char) {
              break;
            }
            return (this.token = Token.Error);
          }
        } while (isWord(this.char));

        this.word = this.input.substring(this.start, this.end);

        switch (this.word.toUpperCase()) {
          case "AND":
            return (this.token = Token.And);
          case "OR":
            return (this.token = Token.Or);
        }

        this.matchLevel = Match.Part;

        if (this.word.length >= 128) {
          this.error = ERROR_OVERLONG_WORD;
          return (this.token = Token.Error);
        }

        return (this.token = Token.Word);
    }
  }
}
